import {
  Alert,
  Button,
  Card,
  Col,
  Row,
  Typography,
  Spin,
  Space,
  Tag,
} from "antd";
import {
  FileTextOutlined,
  CalendarOutlined,
  QuestionCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  EditOutlined,
  TeamOutlined,
  RiseOutlined,
  HourglassOutlined,
  FieldTimeOutlined,
} from "@ant-design/icons";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useGetDashboardStatistics } from "@/hooks/dashboard/useGetDashboardStatistics";
import { useMemo, type ReactNode } from "react";
import { useExamStore, ThemeMode } from "@store";
import { useThemeDetector } from "@hooks";
import { useNavigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { getKeycloakRealm } from "@/components/helpers";

const Chart = lazy(() => import("react-apexcharts"));

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.35, delay },
});

export const DashboardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: statistics, isLoading } = useGetDashboardStatistics();
  const theme = useExamStore((state) => state.theme);
  const { isDarkTheme } = useThemeDetector();

  const isDark = theme === ThemeMode.Dark || (theme === ThemeMode.System && isDarkTheme);

  const cardBg = isDark ? "#1f1f1f" : "#ffffff";
  const alertBg = isDark ? "#2a1f00" : "#fffbe6";
  const chartForeColor = isDark ? "#d0d0d0" : "#444";
  const gridColor = isDark ? "#2d2d2d" : "#f0f0f0";

  // ── Candidates by month – stacked bar ───────────────────────────────────
  const candidatesChart = useMemo(() => {
    const months = statistics?.candidatesByMonth?.map((d) => d.month) ?? [];
    return {
      options: {
        chart: {
          id: "candidates-chart",
          type: "bar" as const,
          stacked: true,
          background: "transparent",
          foreColor: chartForeColor,
          toolbar: { show: false },
        },
        theme: { mode: isDark ? ("dark" as const) : ("light" as const) },
        plotOptions: { bar: { borderRadius: 3, columnWidth: "55%" } },
        dataLabels: { enabled: false },
        xaxis: {
          categories: months,
          labels: { style: { colors: chartForeColor, fontSize: "11px" }, rotate: -30 },
        },
        yaxis: { labels: { style: { colors: chartForeColor } } },
        legend: { position: "top" as const, labels: { colors: chartForeColor } },
        colors: ["#52c41a", "#faad14", "#ff4d4f", "#1677ff"],
        grid: { borderColor: gridColor },
        tooltip: { shared: true, intersect: false },
      },
      series: [
        {
          name: t("pages.dashboard.candidatesPassed"),
          data: statistics?.candidatesByMonth?.map((d) => d.passed) ?? [],
        },
        {
          name: t("pages.dashboard.candidatesWaitingCorrection"),
          data: statistics?.candidatesByMonth?.map((d) => d.waitingCorrection) ?? [],
        },
        {
          name: t("pages.dashboard.candidatesNoShow"),
          data: statistics?.candidatesByMonth?.map((d) => d.noShow) ?? [],
        },
        {
          name: t("pages.dashboard.candidatesInProgress"),
          data:
            statistics?.candidatesByMonth?.map(
              (d) => Math.max(0, d.total - d.passed - d.waitingCorrection - d.noShow)
            ) ?? [],
        },
      ],
    };
  }, [statistics?.candidatesByMonth, isDark, chartForeColor, gridColor, t]);

  // ── Exam terms by month – bar ────────────────────────────────────────────
  const barChartData = useMemo(() => {
    const categories = statistics?.examTermsByMonth?.map((d) => d.month) ?? [];
    const data = statistics?.examTermsByMonth?.map((d) => d.count) ?? [];
    return {
      options: {
        chart: {
          id: "exam-terms-chart",
          background: "transparent",
          foreColor: chartForeColor,
          toolbar: { show: false },
        },
        theme: { mode: isDark ? ("dark" as const) : ("light" as const) },
        plotOptions: { bar: { borderRadius: 4, columnWidth: "60%" } },
        dataLabels: { enabled: false },
        xaxis: { categories, labels: { style: { colors: chartForeColor } } },
        yaxis: { labels: { style: { colors: chartForeColor } } },
        colors: ["#F1B80E"],
        grid: { borderColor: gridColor },
      },
      series: [{ name: t("pages.dashboard.examTerms"), data }],
    };
  }, [statistics?.examTermsByMonth, isDark, chartForeColor, gridColor, t]);

  // ── Questions by type – donut ────────────────────────────────────────────
  const donutChartData = useMemo(() => {
    const labels = statistics?.questionsByType?.map((d) => d.type) ?? [];
    const series = statistics?.questionsByType?.map((d) => d.count) ?? [];
    return {
      options: {
        chart: { id: "questions-type-chart", background: "transparent", foreColor: chartForeColor },
        theme: { mode: isDark ? ("dark" as const) : ("light" as const) },
        labels,
        colors: ["#F1B80E", "#3498db", "#2ecc71", "#e74c3c", "#9b59b6"],
        legend: { position: "bottom" as const, labels: { colors: chartForeColor } },
        dataLabels: { enabled: true },
        plotOptions: {
          pie: {
            donut: {
              size: "65%",
              labels: {
                show: true,
                total: { show: true, label: t("pages.dashboard.total"), color: chartForeColor },
              },
            },
          },
        },
      },
      series,
    };
  }, [statistics?.questionsByType, isDark, chartForeColor, t]);

  // ── Questions by category – horizontal bar ───────────────────────────────
  const horizontalBarData = useMemo(() => {
    const categories = statistics?.questionsByCategory?.map((d) => d.category) ?? [];
    const data = statistics?.questionsByCategory?.map((d) => d.count) ?? [];
    return {
      options: {
        chart: {
          id: "questions-category-chart",
          background: "transparent",
          foreColor: chartForeColor,
          toolbar: { show: false },
        },
        theme: { mode: isDark ? ("dark" as const) : ("light" as const) },
        plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: "70%" } },
        dataLabels: { enabled: false },
        xaxis: { categories, labels: { style: { colors: chartForeColor } } },
        yaxis: { labels: { style: { colors: chartForeColor } } },
        colors: ["#3498db"],
        grid: { borderColor: gridColor },
      },
      series: [{ name: t("pages.dashboard.questions"), data }],
    };
  }, [statistics?.questionsByCategory, isDark, chartForeColor, gridColor, t]);

  const ChartLoader = () => (
    <div style={{ height: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Spin />
    </div>
  );

  // Reusable, uniformly-sized KPI card (icon tile + value + single-line label)
  const StatCard = ({
    icon,
    color,
    bg,
    borderColor,
    label,
    value,
  }: {
    icon: ReactNode;
    color: string;
    bg?: string;
    borderColor?: string;
    label: string;
    value: ReactNode;
  }) => (
    <Card
      style={{ background: bg ?? cardBg, borderColor, height: "100%" }}
      styles={{ body: { padding: 16 } }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 10,
            background: color + "22",
            color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 20,
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1.15, color }}>
            {isLoading ? <Spin size="small" /> : value}
          </div>
          <Typography.Text
            type="secondary"
            title={label}
            style={{
              fontSize: 12,
              display: "block",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {label}
          </Typography.Text>
        </div>
      </div>
    </Card>
  );

  const needsAction =
    (statistics?.termsNeedingCompletion ?? 0) > 0 || (statistics?.pendingCorrections ?? 0) > 0;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 1 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <Typography.Title level={2} style={{ margin: 0 }}>
          {t("pages.dashboard.title")}
        </Typography.Title>
        <Tag color={isDark ? "gold" : "orange"} style={{ fontSize: 12 }}>
          {new Date().toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
        </Tag>
      </div>

      {/* ── Action alerts ─────────────────────────────────────────────────── */}
      {!isLoading && needsAction && (
        <motion.div {...fadeUp(0)}>
          <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
            {(statistics?.termsNeedingCompletion ?? 0) > 0 && (
              <Col xs={24} md={12}>
                <Alert
                  type="warning"
                  showIcon
                  icon={<WarningOutlined />}
                  message={
                    <span>
                      <strong>{statistics?.termsNeedingCompletion}</strong>{" "}
                      {t("pages.dashboard.alertTermsNeedingCompletion")}
                    </span>
                  }
                  action={
                    <Button size="small" type="link" onClick={() => navigate(`/${getKeycloakRealm}/exam-periods`)}>
                      {t("pages.dashboard.viewAll")}
                    </Button>
                  }
                  style={{ background: alertBg }}
                />
              </Col>
            )}
            {(statistics?.pendingCorrections ?? 0) > 0 && (
              <Col xs={24} md={12}>
                <Alert
                  type="info"
                  showIcon
                  icon={<EditOutlined />}
                  message={
                    <span>
                      <strong>{statistics?.pendingCorrections}</strong>{" "}
                      {t("pages.dashboard.alertPendingCorrections")}
                    </span>
                  }
                  action={
                    <Button size="small" type="link" onClick={() => navigate(`/${getKeycloakRealm}/grading/pending-corrections`)}>
                      {t("pages.dashboard.viewAll")}
                    </Button>
                  }
                />
              </Col>
            )}
          </Row>
        </motion.div>
      )}

      {/* ── Action-required KPIs ──────────────────────────────────────────── */}
      <motion.div {...fadeUp(0.05)}>
        <Row gutter={[14, 14]} style={{ marginBottom: 14 }}>
          <Col xs={12} sm={12} lg={6}>
            <StatCard
              icon={<FieldTimeOutlined />}
              color="#1677ff"
              bg={isDark ? "#0d2137" : "#e6f4ff"}
              borderColor="#1677ff"
              label={t("pages.dashboard.termsInProgressToday")}
              value={statistics?.termsInProgressToday ?? 0}
            />
          </Col>
          <Col xs={12} sm={12} lg={6}>
            <StatCard
              icon={<CalendarOutlined />}
              color="#722ed1"
              bg={isDark ? "#1a1030" : "#f9f0ff"}
              borderColor="#722ed1"
              label={t("pages.dashboard.upcomingTerms")}
              value={statistics?.upcomingTerms ?? 0}
            />
          </Col>
          <Col xs={12} sm={12} lg={6}>
            <StatCard
              icon={<ClockCircleOutlined />}
              color="#faad14"
              bg={isDark ? "#2a1a00" : "#fffbe6"}
              borderColor={(statistics?.termsNeedingCompletion ?? 0) > 0 ? "#faad14" : undefined}
              label={t("pages.dashboard.termsNeedingCompletion")}
              value={statistics?.termsNeedingCompletion ?? 0}
            />
          </Col>
          <Col xs={12} sm={12} lg={6}>
            <StatCard
              icon={<HourglassOutlined />}
              color="#1677ff"
              bg={isDark ? "#001a2c" : "#f0f9ff"}
              borderColor={(statistics?.pendingCorrections ?? 0) > 0 ? "#1677ff" : undefined}
              label={t("pages.dashboard.pendingCorrections")}
              value={statistics?.pendingCorrections ?? 0}
            />
          </Col>
        </Row>
      </motion.div>

      {/* ── Totals KPIs ───────────────────────────────────────────────────── */}
      <motion.div {...fadeUp(0.1)}>
        <Row gutter={[14, 14]} style={{ marginBottom: 16 }}>
          <Col xs={12} sm={12} lg={6}>
            <StatCard
              icon={<FileTextOutlined />}
              color="#13c2c2"
              label={t("pages.dashboard.totalExams")}
              value={statistics?.totalExams ?? 0}
            />
          </Col>
          <Col xs={12} sm={12} lg={6}>
            <StatCard
              icon={<CalendarOutlined />}
              color="#2f54eb"
              label={t("pages.dashboard.totalExamTerms")}
              value={statistics?.totalExamTerms ?? 0}
            />
          </Col>
          <Col xs={12} sm={12} lg={6}>
            <StatCard
              icon={<TeamOutlined />}
              color="#52c41a"
              label={t("pages.dashboard.totalParticipants")}
              value={statistics?.totalParticipants ?? 0}
            />
          </Col>
          <Col xs={12} sm={12} lg={6}>
            <StatCard
              icon={<QuestionCircleOutlined />}
              color="#eb2f96"
              label={t("pages.dashboard.totalQuestions")}
              value={statistics?.totalQuestions ?? 0}
            />
          </Col>
        </Row>
      </motion.div>

      {/* ── Candidates – annual overview ─────────────────────────────────── */}
      <motion.div {...fadeUp(0.1)}>
        <Card
          title={
            <Space>
              <RiseOutlined style={{ color: "#52c41a" }} />
              {t("pages.dashboard.candidatesAnnualOverview")}
            </Space>
          }
          style={{ marginBottom: 16 }}
          loading={isLoading}
          extra={
            <Space size={16} wrap>
              <Space size={4}><span style={{ color: "#52c41a" }}>●</span><Typography.Text type="secondary" style={{ fontSize: 12 }}>{t("pages.dashboard.candidatesPassed")}</Typography.Text></Space>
              <Space size={4}><span style={{ color: "#faad14" }}>●</span><Typography.Text type="secondary" style={{ fontSize: 12 }}>{t("pages.dashboard.candidatesWaitingCorrection")}</Typography.Text></Space>
              <Space size={4}><span style={{ color: "#ff4d4f" }}>●</span><Typography.Text type="secondary" style={{ fontSize: 12 }}>{t("pages.dashboard.candidatesNoShow")}</Typography.Text></Space>
              <Space size={4}><span style={{ color: "#1677ff" }}>●</span><Typography.Text type="secondary" style={{ fontSize: 12 }}>{t("pages.dashboard.candidatesInProgress")}</Typography.Text></Space>
            </Space>
          }
        >
          {!isLoading && (
            <Suspense fallback={<ChartLoader />}>
              <Chart
                options={candidatesChart.options}
                series={candidatesChart.series}
                type="bar"
                height={320}
              />
            </Suspense>
          )}
        </Card>
      </motion.div>

      {/* ── Exam terms by month + Questions by type ──────────────────────── */}
      <motion.div {...fadeUp(0.15)}>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} lg={14}>
            <Card title={t("pages.dashboard.examTermsByMonth")} loading={isLoading} style={{ background: cardBg }}>
              {!isLoading && (
                <Suspense fallback={<ChartLoader />}>
                  <Chart
                    options={barChartData.options}
                    series={barChartData.series}
                    type="bar"
                    height={280}
                  />
                </Suspense>
              )}
            </Card>
          </Col>

          <Col xs={24} lg={10}>
            <Card title={t("pages.dashboard.questionsByType")} loading={isLoading} style={{ background: cardBg }}>
              {!isLoading && donutChartData.series.length > 0 ? (
                <Suspense fallback={<ChartLoader />}>
                  <Chart
                    options={donutChartData.options}
                    series={donutChartData.series}
                    type="donut"
                    height={280}
                  />
                </Suspense>
              ) : !isLoading ? (
                <div style={{ height: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography.Text type="secondary">{t("pages.dashboard.noData")}</Typography.Text>
                </div>
              ) : null}
            </Card>
          </Col>
        </Row>
      </motion.div>

      {/* ── Questions by category ────────────────────────────────────────── */}
      <motion.div {...fadeUp(0.2)}>
        <Card
          title={t("pages.dashboard.questionsByCategory")}
          loading={isLoading}
          style={{ background: cardBg }}
        >
          {!isLoading && horizontalBarData.series[0].data.length > 0 ? (
            <Suspense fallback={<ChartLoader />}>
              <Chart
                options={horizontalBarData.options}
                series={horizontalBarData.series}
                type="bar"
                height={280}
              />
            </Suspense>
          ) : !isLoading ? (
            <div style={{ height: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Typography.Text type="secondary">{t("pages.dashboard.noData")}</Typography.Text>
            </div>
          ) : null}
        </Card>
      </motion.div>
    </motion.div>
  );
};
